import { TFunction } from '@lyearn/i18n';
import { rearrangeItems } from '@/components/DragDrop';
import { StatusTypes } from '@/components/EditorBuilder/PowerUps/enums';
import { getBlockByType } from '@/components/EditorBuilder/utils';
import { Blocks } from '@/components/PageEditor/types';
import {
  generateLesson,
  generateSection,
} from '@/modules/corporate/modules/course/views/CourseBuilder/components/Sidebar/stub';
import { ContentType } from '@/modules/corporate/modules/course/views/CourseBuilder/components/Sidebar/types';

import { CourseError } from '../enums';
import {
  onAddLessonPayloadType,
  onImportLessonPayloadType,
  onMovePayloadType,
  onRemovePayloadType,
  onRenameItemPayloadType,
  onSetLessonCoverPayloadType,
  onSetLessonCustomDurationSavePayloadType,
  onSetLessonOptionalPayloadType,
  onSetLessonQuestionsPayloadType,
  onSetLessonUpdatedAtAttrPayloadType,
} from '../types';
import { findAllContentByType, findContentByType, getBlockId, getLessonPageId } from './adapt';
import { getIsValidMove, getMoveItems } from './dnd';
import { getLessonOrderPreference, LessonOrderPreferenceEnum } from './lessonOrderPreference';

export {
  getBlockByType,
  onSetCommentThreadId,
  onSetImportLessonAttrs,
  onSetLessonAssignee,
  onUpdateContentText,
  onUpdateLessonStatus,
} from '@/components/EditorBuilder/utils';

//id, blockId, name and description
export const getLessonInfo = (lesson?: ContentType) =>
  lesson
    ? {
        id: getLessonPageId(lesson),
        blockId: getBlockId(lesson),
        name: findContentByType(lesson?.content, Blocks.TITLE)?.content?.[0]?.text,
        description: findContentByType(lesson?.content, Blocks.DESCRIPTION)?.content?.[0]?.text,
      }
    : undefined;

export const getLessonsParent = (data?: ContentType) => {
  const sections = data?.content ?? [];
  return sections.reduce(
    (allLessonsParent, section) => {
      const lessons = findAllContentByType(section.content, Blocks.LESSON);
      const sectionId = getItemId(section);

      const lessonsParent = lessons.reduce(
        (lessonsParent, lesson) => {
          lessonsParent[getLessonPageId(lesson)!] = sectionId!;
          const subLessons = findAllContentByType(lesson.content, Blocks.LESSON);
          const lessonId = getLessonPageId(lesson);

          const subLessonsParent = subLessons.reduce(
            (subLessonsParent, subLesson) => {
              subLessonsParent[getLessonPageId(subLesson)!] = lessonId!;
              return subLessonsParent;
            },
            {} as { [key: string]: string },
          );

          return { ...lessonsParent, ...subLessonsParent };
        },
        {} as { [key: string]: string },
      );

      return { ...allLessonsParent, ...lessonsParent };
    },
    {} as { [key: string]: string },
  );
};

export const getSequentialSectionsAndLessons = (data?: ContentType) => {
  const sections = data?.content ?? [];

  return sections.flatMap((section) => {
    const lessons = findAllContentByType(section.content, Blocks.LESSON);

    const lessonsOrSubLessons = lessons.flatMap((lessonOrSubLesson) => [
      lessonOrSubLesson,
      ...findAllContentByType(lessonOrSubLesson.content, Blocks.LESSON),
    ]);
    return [section, ...lessonsOrSubLessons];
  });
};

export const getRootLessons = (data?: ContentType) => {
  const sections = data?.content ?? [];
  return sections?.flatMap((section) => findAllContentByType(section.content, Blocks.LESSON));
};

export const getLessonDetails = (lessonContent?: ContentType[]) => {
  const lessonDetails = lessonContent?.reduce(
    (details, item) => {
      if (item.type !== Blocks.LESSON) {
        details[item.type] = item;
      }
      return details;
    },
    {} as { [key: string]: ContentType },
  );

  return {
    title: lessonDetails?.[Blocks.TITLE]?.content?.[0]?.text ?? '',
    description: lessonDetails?.[Blocks.DESCRIPTION]?.content?.[0]?.text ?? '',
    cover: {
      src: lessonDetails?.[Blocks.COVER]?.attrs?.src ?? '',
      yOffsetPercent: lessonDetails?.[Blocks.COVER]?.attrs?.yOffsetPercent ?? 0,
    },
    commentThreadId: lessonDetails?.[Blocks.LESSON_COMMENTS]?.attrs?.commentThreadId ?? '',
    assignee: { id: lessonDetails?.[Blocks.LESSON_ASSIGNEE]?.attrs?.userId },
    status: lessonDetails?.[Blocks.LESSON_STATUS]?.attrs?.status as StatusTypes,
    notes: lessonDetails?.[Blocks.LESSON_NOTES]?.content?.[0]?.text ?? '',
    uploadFileUserId: lessonDetails?.[Blocks.REF]?.attrs?.uploadFileUserId,
    uploadFileUrl: lessonDetails?.[Blocks.REF]?.attrs?.uploadFileUrl,
    uploadFileJobId: lessonDetails?.[Blocks.REF]?.attrs?.uploadFileJobId,
  };
}; //except inner lessons

//for section and lesson content
export const findContentById = (content?: ContentType[], id?: string) =>
  content?.find((item) => getItemId(item) === id);

//for section and lesson
export const getItemId = (content?: ContentType) =>
  content?.type === Blocks.SECTION ? content.attrs?.id : getLessonPageId(content);

export const getLessonIndex = (lessons: (ContentType | undefined)[], lessonId?: string) => {
  return lessons?.findIndex((lesson) => getLessonPageId(lesson) === lessonId);
};

export const getSection = (data?: ContentType, sectionId?: string) =>
  data?.content?.find((sec) => getItemId(sec) === sectionId);

export const getSectionFromLesson = (sections?: ContentType[], lessonPageId?: string | null) =>
  sections?.find(
    (sec) => sec?.content?.findIndex((lesson) => getLessonPageId(lesson) === lessonPageId) !== -1,
  );

export const onAddSection = (data?: ContentType, t?: TFunction) => {
  const newSection = generateSection(t);
  data?.content!.push(newSection);
  return getLessonPageId(findContentByType(newSection.content, Blocks.LESSON));
};

export const onAddLesson = (
  payload: onAddLessonPayloadType,
  sections: ContentType[],
  data?: ContentType,
  lessons?: ContentType[],
  t?: TFunction,
  courseId?: string,
) => {
  const id = payload.id ?? sections[sections.length - 1]?.attrs?.id!;

  if (lessons?.length === 0) {
    return onAddSection(data, t);
  }
  const currentSectionOrLesson =
    payload.type === Blocks.LESSON ? lessons?.[getLessonIndex(lessons, id)] : getSection(data, id);
  const newLesson = generateLesson(t);

  // Get lesson order preference from localStorage
  const preference = getLessonOrderPreference(courseId);

  if (preference === LessonOrderPreferenceEnum.AddToTop) {
    currentSectionOrLesson?.content?.unshift(newLesson);
  } else {
    currentSectionOrLesson?.content?.push(newLesson);
  }

  return getLessonPageId(newLesson);
};

export const onImportLesson = (
  t: TFunction,
  payload: onImportLessonPayloadType,
  sections: ContentType[],
  data?: ContentType,
) => {
  const lastSection = getSection(data, sections[sections.length - 1]?.attrs?.id!);
  const newLesson = generateLesson(
    t,
    payload.title,
    payload.uploadFileUrl,
    payload.uploadFileUserId,
  );
  lastSection?.content?.push(newLesson);
  return getLessonPageId(newLesson);
};

export const onRenameSectionOrLesson = (
  payload: onRenameItemPayloadType,
  data?: ContentType,
  type?: Blocks,
  lessons?: ContentType[],
) => {
  const { id, value } = payload;
  const sectionOrLesson =
    type === Blocks.SECTION ? getSection(data, id) : lessons?.[getLessonIndex(lessons, id)];
  const title = getBlockByType(sectionOrLesson?.content, Blocks.TITLE);
  if (title) {
    title.text = value;
  }
};

export const onSetLessonCover = (
  payload: onSetLessonCoverPayloadType,
  lessonContent?: ContentType[],
) => {
  const { picture, yOffsetPercent } = payload;
  const coverAttributes = findContentByType(lessonContent, Blocks.COVER)?.attrs;
  if (coverAttributes) {
    coverAttributes.src = picture;
    coverAttributes.yOffsetPercent = yOffsetPercent;
  }
};

export const onSetLessonCustomDurationSave = (
  payload: onSetLessonCustomDurationSavePayloadType,
  lessonContent?: ContentType,
) => {
  const lessonAttributes = lessonContent?.attrs;
  if (lessonAttributes) {
    lessonAttributes.customDuration = payload.customDuration;
  }
};

export const onSetLessonUpdatedAtAttr = (
  payload: onSetLessonUpdatedAtAttrPayloadType,
  lessonContent?: ContentType,
) => {
  const lessonAttributes = lessonContent?.attrs;
  if (lessonAttributes) {
    lessonAttributes.updatedAt = payload.updatedAt;
  }
};

export const onSetLessonQuestions = (
  payload: onSetLessonQuestionsPayloadType,
  lessonContent?: ContentType,
) => {
  const lessonAttributes = lessonContent?.attrs;
  if (lessonAttributes) {
    lessonAttributes.questionIds = payload.ids;
  }
};

export const areAllLessonsOptional = (lessons?: ContentType[]) => {
  return lessons?.reduce(
    (IsAllOptional, lesson) => IsAllOptional && !!lesson.attrs?.isOptional,
    true,
  );
};

export const onSetLessonOptional = (
  payload: onSetLessonOptionalPayloadType,
  lessons?: ContentType[],
) => {
  const currentSectionOrLesson = lessons?.[getLessonIndex(lessons, payload.id)];
  const lessonAttributes = currentSectionOrLesson?.attrs;
  if (lessonAttributes) {
    lessonAttributes.isOptional = !lessonAttributes.isOptional;
  }
};

export const onRemove = (
  payload: onRemovePayloadType,
  lessonsParent: ReturnType<typeof getLessonsParent>,
  data?: ContentType,
  sequentialSectionsAndLessons?: ContentType[],
  sections?: ContentType[],
) => {
  const { id, type } = payload;
  const parentId = lessonsParent[payload?.id!];
  //throw error if only lesson
  if (type === Blocks.LESSON && getSection(data, parentId) !== undefined) {
    const section = getSection(data, parentId);
    if (findAllContentByType(section?.content, Blocks.LESSON).length === 1) {
      throw new Error(CourseError.SectionEmpty);
    }
  }
  if (type === Blocks.SECTION && sections?.length === 1) {
    throw new Error(CourseError.CourseEmpty);
  }
  //get parent content: courseContent for section, section for lessons, lessons for sublessons
  const parentContent =
    type === Blocks.SECTION
      ? data?.content
      : sequentialSectionsAndLessons?.find((secOrLesson) => getItemId(secOrLesson) === parentId)
          ?.content;
  //get index in parentContent
  const currentIndex = parentContent?.findIndex((secOrLesson) => getItemId(secOrLesson) === id);

  if (currentIndex !== undefined && currentIndex !== -1) {
    parentContent?.splice(currentIndex, 1);
  }
};

export const onMove = (
  payload: onMovePayloadType,
  lessonsParent: ReturnType<typeof getLessonsParent>,
  data?: ContentType,
  sequentialItems?: ContentType[],
  sections?: ContentType[],
  rootLessons?: ContentType[],
) => {
  const { id, addLevel, toId } = payload;
  const movedItem = findContentById(sequentialItems, id)!;
  const dropOnItem = findContentById(sequentialItems, toId);
  const _onRemove = () =>
    onRemove(
      { id, type: movedItem?.type as Blocks },
      lessonsParent,
      data,
      sequentialItems,
      sections,
    );

  const isValidMove = getIsValidMove(id, movedItem, addLevel, lessonsParent, rootLessons); //is dragging item across that level valid?

  if (isValidMove) {
    const { fromItem, toItem } = getMoveItems(
      payload,
      lessonsParent,
      movedItem,
      dropOnItem,
      data,
      sequentialItems,
      rootLessons,
    );
    rearrangeItems(fromItem!, toItem!, movedItem, dropOnItem, _onRemove);
  }
};
